<template>
    <b-overlay :show="isLoading" class="rounded" opacity="0.2">
        <b-card no-body>
            <b-card-body>

                <div v-if="editing" class="alert alert-warning d-flex align-items-center py-50 mb-1">
                    <feather-icon icon="AlertTriangleIcon" size="18" class="mr-50" />
                    <span><strong>Düzenleme modu:</strong> Mevcut yakıt çıkış kaydı düzenleniyor. Araç, plaka, yakıt tipi ve yakıt deposu değiştirilemez.</span>
                </div>

                <div class="row mb-2 pb-75 border-bottom">
                    <div class="col-md-12 mb-1">
                        <label class="form-label">İşlem Numarası</label>
                        <input type="text" class="form-control" v-model="form.ProcessNumber" disabled />
                    </div>

                    <div class="col-md-6 mb-1">
                        <label class="form-label">Araç Plakası <span class="text-danger">*</span></label>
                        <v-validator :errors="plateErrors" parameter="VehiclePlateId" :watch="form.VehiclePlateId">
                            <v-select-list :value="form.VehiclePlateId" :options="truckPlateOptions" :loading="truckPlateLoading" @input="handlePlateSelection" label="PlateValue" :reduce="(plate) => plate.Id" placeholder="Plaka seçiniz..." :disabled="editing" />
                        </v-validator>
                    </div>

                    <div class="col-md-6 mb-1">
                        <v-validator :errors="plateErrors" parameter="TrailerPlateId" :watch="form.TrailerPlateId">
                            <label class="form-label">Treyler Plakası</label>
                            <v-select-list v-model="form.TrailerPlateId" :options="trailerPlateOptions" :loading="trailerPlateLoading" :disabled="!form.VehiclePlateId || editing" label="PlateValue" :reduce="(plate) => plate.Id" placeholder="Treyler plakası (isteğe bağlı)" @input="onTrailerPlateChange" />
                        </v-validator>
                    </div>

                    <div v-if="form.VehiclePlateId" class="col-12 mb-1">
                        <label class="form-label">Mevcut Konum (Adres)</label>
                        <input type="text" class="form-control" v-model="form.Address" :disabled="locStatus.isLocked" placeholder="Konum bilgisi bekleniyor..." @input="handleManualInput('loc', $event.target.value)" />
                        <small v-if="locStatus.message" :class="`text-${locStatus.variant} font-weight-bold`"> <feather-icon :icon="locStatus.variant === 'danger' ? 'AlertCircleIcon' : 'InfoIcon'" size="10" /> {{ locStatus.message }} </small>
                    </div>

                    <template v-if="!form.VehiclePlateId">
                        <div class="col-md-6 mb-1">
                            <label class="form-label">Araç KM (Manuel)</label>
                            <VCurrencyInput :value="form.VehicleCurrentKMManual" @update-value="form.VehicleCurrentKMManual = $event" placeholder="Manuel KM giriniz" />
                        </div>
                        <div class="col-md-6 mb-1">
                            <label class="form-label">Sürücü <span class="text-danger">*</span></label>
                            <v-select-list v-model="form.DriverEmployeeId" :options="computedEmployeeOptions" :loading="computedEmployeeLoading" @search="searchEmployees" label="FullName" :reduce="(employee) => employee.Id" placeholder="Sürücü adı girin…" />
                        </div>
                    </template>

                    <div v-else class="col-12">
                        <div class="row">
                            <div v-if="form.SeyirDriverFullName" class="col-md-6 mb-1">
                                <label class="form-label text-primary">Seyir Sürücü Bilgisi</label>
                                <input type="text" class="form-control" :value="form.SeyirDriverFullName" disabled readonly />
                            </div>

                            <div :class="form.SeyirDriverFullName ? 'col-md-6' : 'col-md-12'" class="mb-1">
                                <label class="form-label">Sürücü <span class="text-danger">*</span></label>
                                <v-select-list v-model="form.DriverEmployeeId" :options="computedEmployeeOptions" :loading="computedEmployeeLoading" @search="searchEmployees" label="FullName" :reduce="(employee) => employee.Id" placeholder="Sürücü adı girin…" />
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-md-4 mb-1">
                                <label class="form-label">Seyir Araç Güncel KM</label>
                                <VCurrencyInput
                                    :value="form.VehicleCurrentKM"
                                    @update-value="
                                        form.VehicleCurrentKM = $event;
                                        handleManualInput('km', $event);
                                    "
                                    disabled
                                />
                                <small v-if="kmStatus.message" :class="`text-${kmStatus.variant} font-weight-bold`"> <feather-icon :icon="kmStatus.variant === 'danger' ? 'AlertCircleIcon' : 'InfoIcon'" size="10" /> {{ kmStatus.message }} </small>
                            </div>

                            <div class="col-md-4 mb-1">
                                <label class="form-label">Araç KM (Manuel)</label>
                                <VCurrencyInput :value="form.VehicleCurrentKMManual" @update-value="form.VehicleCurrentKMManual = $event" placeholder="Manuel KM giriniz" />
                            </div>

                            <div class="col-md-4 mb-1">
                                <label class="form-label">Araç Klima Güncel Sayaç</label>
                                <VCurrencyInput ref="acCounterInput" :value="form.VehicleAcCurrentCounter" @update-value="handleAcCounterInput($event)" />
                                <small v-if="acCounterStatus.message" :class="`text-${acCounterStatus.variant} font-weight-bold`"> <feather-icon icon="AlertCircleIcon" size="10" /> {{ acCounterStatus.message }} </small>
                            </div>
                        </div>
                    </div>
                </div>

                <h5 class="mb-1">Araç Yakıt Girişleri</h5>
                <div v-for="(detail, index) in form.FuelStorageTransactionDetails" :key="index" class="detail-card border rounded p-1 mb-2 position-relative">
                    <div class="row align-items-end">
                        <div class="col-12 col-md-4 mb-0">
                            <label class="form-label">Yakıt Tipi <span class="text-danger">*</span></label>
                            <v-select v-model="detail.FuelTypeId" :options="fuelTypeOptions" label="text" :reduce="(item) => item.value" placeholder="Yakıt tipi seçiniz..." @input="onFuelTypeChange(detail)" :disabled="editing" />
                        </div>
                        <div class="col-12 col-md-6 mb-0">
                            <label class="form-label">Yakıt Deposu <span class="text-danger">*</span></label>
                            <div class="depot-select-wrapper">
                                <v-select
                                    v-model="detail.FuelStorageId"
                                    :options="filteredFuelStorageOptions(detail.FuelTypeId)"
                                    label="DisplayName"
                                    :reduce="(item) => item.Id"
                                    placeholder="Depo seçiniz..."
                                    :disabled="!detail.FuelTypeId || editing"
                                    v-b-tooltip.hover.bottom="getDepotName(detail.FuelStorageId)"
                                    v-on:input="onDepotSelect(detail)"
                                />
                            </div>
                        </div>
                        <div class="col-12 col-md-2 mb-0 d-flex justify-content-end">
                            <button type="button" class="btn btn-outline-danger px-1 flex-grow-1 flex-md-grow-0" v-on:click="removeDetail(index)" :disabled="form.FuelStorageTransactionDetails.length <= 1 || editing">
                                <feather-icon icon="TrashIcon" size="16" />
                            </button>
                            <button type="button" class="btn btn-outline-primary px-1 ml-1 flex-grow-1 flex-md-grow-0" v-on:click="addNewDetail" v-if="index === form.FuelStorageTransactionDetails.length - 1" :disabled="editing">
                                <feather-icon icon="PlusIcon" size="16" />
                            </button>
                        </div>
                    </div>
                    <div class="row mb-0">
                        <div v-if="detail.FuelStorageId && getDepotInfo(detail.FuelStorageId)" class="col-10 d-flex justify-content-center mb-0 mt-25 ml-2">
                            <div>
                                <strong class="font-weight-bold text-primary">Depodaki Mevcut Yakıt:</strong>
                                <span class="badge badge-light-primary ml-50"> {{ (getDepotInfo(detail.FuelStorageId).FuelQuantity || 0).toLocaleString("tr-TR") }} lt </span>
                            </div>
                        </div>
                    </div>

                    <transition name="fade" mode="out-in">
                        <div v-if="detail.FuelTypeId === '3a82d544-bfda-4995-ad74-29982d9606df' || detail.FuelTypeId === 'b8694977-e4fc-4c90-b02f-d7bc04caa146'" key="pump-info" class="mt-1">
                            <div class="detail-input-section border rounded p-1">
                                <h6 class="form-section-title mb-2">Pompa Bilgileri ({{ detail.FuelTypeId === "3a82d544-bfda-4995-ad74-29982d9606df" ? "Motorin" : "AdBlue" }})</h6>
                                <div v-if="isPumpAmountExceedingStock(detail)" class="alert alert-danger p-1 text-center small mt-1" role="alert">
                                    <feather-icon icon="AlertTriangleIcon" size="14" class="me-1"></feather-icon>
                                    Girilen pompa toplamı, depodaki mevcut yakıttan fazla olamaz!
                                </div>
                                <div class="row">
                                    <div class="col-md-3">
                                        <label class="form-label">Pompa 1 Miktar [lt]</label>
                                        <VCurrencyInput :value="detail.FuelPump1LT" @update-value="detail.FuelPump1LT = $event" />
                                    </div>
                                    <div class="col-md-3">
                                        <label class="form-label">Pompa 1 Sayaç [lt]</label>
                                        <VCurrencyInput :value="detail.FuelPump1Counter" @update-value="detail.FuelPump1Counter = $event" :disabled="true" />
                                    </div>
                                    <div class="col-md-3">
                                        <label class="form-label">Pompa 2 Miktar [lt]</label>
                                        <VCurrencyInput :value="detail.FuelPump2LT" @update-value="detail.FuelPump2LT = $event" />
                                    </div>
                                    <div class="col-md-3">
                                        <label class="form-label">Pompa 2 Sayaç [lt]</label>
                                        <VCurrencyInput :value="detail.FuelPump2Counter" @update-value="detail.FuelPump2Counter = $event" :disabled="true" />
                                    </div>
                                </div>

                                <div v-if="detail.FuelTypeId === '3a82d544-bfda-4995-ad74-29982d9606df'" class="row mt-1">
                                    <div class="col-12">
                                        <div class="form-check form-check-inline">
                                            <input class="form-check-input" type="checkbox" v-model="detail.showAcInputs" :id="`check-ac-${index}`" @change="onShowAcInputsChange(detail)" />
                                            <label class="form-check-label" :for="`check-ac-${index}`">Klima Yakıtı Ekle</label>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="detail.showAcInputs" class="mt-1 pt-1 border-top">
                                    <div class="row">
                                        <div class="col-md-6">
                                            <label class="form-label">Pompa 1 - Klima Miktar [lt]</label>
                                            <VCurrencyInput :value="detail.Ac1FuelPumpLT" @update-value="detail.Ac1FuelPumpLT = $event" />
                                        </div>
                                        <div class="col-md-6">
                                            <label class="form-label">Pompa 2 - Klima Miktar [lt]</label>
                                            <VCurrencyInput :value="detail.Ac2FuelPumpLT" @update-value="detail.Ac2FuelPumpLT = $event" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="text-end mt-2">
                                <strong
                                    >Toplam: <span class="badge bg-primary">{{ getTotalQuantity(detail) }} lt</span></strong
                                >
                            </div>
                        </div>
                    </transition>
                </div>

                <div class="col-12 mb-2 px-0">
                    <div class="text-end p-0 rounded" :style="summaryStyle">
                        <h5 class="mb-0 text-right font-weight-bolder" style="padding: 0.3rem; font-size: small" :class="getGrandTotal >= 0 ? 'text-success' : 'text-danger'">Genel Toplam Çıkış Miktarı: {{ getGrandTotal }} lt</h5>
                    </div>
                </div>

                <div class="col-md-12 px-0">
                    <label class="form-label">Açıklama</label>
                    <textarea class="form-control" rows="2" placeholder="İşlem açıklaması" v-model="form.Description"></textarea>
                </div>
            </b-card-body>
            <b-card-footer>
                <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-outline-secondary" @click="resetForm">Temizle</button>
                    <button type="button" class="btn btn-primary ml-1" @click="showConfirmModal" :disabled="isSaveDisabled">Kaydet</button>
                </div>
            </b-card-footer>
        </b-card>
        <!-- Kayıt Onay Modalı -->
        <b-modal id="withdrawal-confirm-modal" title="Yakıt Çıkışı Onayı" centered size="md" ok-variant="primary" cancel-variant="outline-secondary" @ok="save" no-close-on-backdrop>
            <div class="text-center mb-1">
                <feather-icon icon="AlertCircleIcon" size="48" class="text-warning" />
            </div>
            <h5 class="text-center mb-1">Yakıt çıkışı kaydedilecektir, onaylıyor musunuz?</h5>
            <div class="confirm-summary border rounded p-1 bg-light">
                <div class="d-flex justify-content-between mb-50" v-if="confirmSummary.plate">
                    <span class="text-muted">Araç Plakası:</span>
                    <strong>{{ confirmSummary.plate }}</strong>
                </div>
                <div class="d-flex justify-content-between mb-50" v-if="confirmSummary.driverName">
                    <span class="text-muted">Sürücü:</span>
                    <strong>{{ confirmSummary.driverName }}</strong>
                </div>
                <div v-for="(detail, idx) in confirmSummary.details" :key="idx" class="d-flex justify-content-between mb-50">
                    <span class="text-muted">{{ detail.fuelTypeName }} ({{ detail.depotName }}):</span>
                    <strong>{{ detail.quantity }} Lt</strong>
                </div>
                <hr class="my-50" />
                <div class="d-flex justify-content-between">
                    <span class="font-weight-bold">Toplam Miktar:</span>
                    <strong class="text-primary">{{ confirmSummary.totalQuantity }} Lt</strong>
                </div>
            </div>
            <template #modal-ok>
                <feather-icon icon="CheckIcon" size="14" class="mr-50" />Onayla ve Kaydet
            </template>
            <template #modal-cancel>
                <feather-icon icon="XIcon" size="14" class="mr-50" />Vazgeç
            </template>
        </b-modal>


    </b-overlay>
</template>

<script>
import { BOverlay, BCard, BCardBody, BCardFooter, VBTooltip } from "bootstrap-vue";
import FuelStorageApi from "@/api/fuel-storage";
import EmployeeOperationsApi from "@/api/employee-operations";
import VehicleOperationsApi from "@/api/vehicle-operations";
import VCurrencyInput from "@/components/VCurrencyInput.vue";
import VValidator from "@/components/VValidator.vue";
import SeyirOperationsApi from "@/api/seyir";

export default {
    name: "FuelWithdrawalPage",
    components: {
        BOverlay,
        BCard,
        BCardBody,
        BCardFooter,
        VCurrencyInput,
        VValidator,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    data() {
        return {
            isLoading: false,
            form: {},
            initialForm: {},
            editing: false,
            isLoadingForEdit: false,
            errors: {},
            plateErrors: {},
            fuelStorageOptions: [],
            employees: [],
            allEmployees: [],
            vehiclePlateOptions: [],
            truckPlateOptions: [],
            trailerPlateOptions: [],
            fuelTypeOptions: [],
            employeeLoading: false,
            vehiclePlateLoading: false,
            truckPlateLoading: false,
            trailerPlateLoading: false,
            kmStatus: { isLocked: false, message: null, variant: null },
            locStatus: { isLocked: false, message: null, variant: null },
            acCounterStatus: { message: null, variant: null },
            lastVehicleAcCounter: null,
        };
    },
    computed: {
        computedEmployeeOptions() {
            return (this.employees || []).filter((emp) => emp.IsWorking);
        },
        computedEmployeeLoading() {
            return this.employeeLoading;
        },
        allPlateOptions() {
            return [...(this.truckPlateOptions || []), ...(this.trailerPlateOptions || [])];
        },
        getGrandTotal() {
            if (!this.form.FuelStorageTransactionDetails) return "0";
            return this.form.FuelStorageTransactionDetails.reduce((total, detail) => {
                return total + this.getTotalQuantityNumeric(detail);
            }, 0).toFixed(2);
        },

        summaryStyle() {
            const style = { transition: "background-color 0.3s ease" };
            if (this.getGrandTotal >= 0) {
                style.backgroundColor = "#d1e7dd";
            } else {
                style.backgroundColor = "#f8d7da";
            }
            return style;
        },
        isSaveDisabled() {
            const form = this.form;
            if (!form || !form.FuelStorageTransactionDetails || !form.VehiclePlateId || !form.DriverEmployeeId) {
                return true;
            }
            return form.FuelStorageTransactionDetails.some((detail) => !detail.FuelTypeId || !detail.FuelStorageId || (detail.Quantity || 0) <= 0);
        },
        confirmSummary() {
            const form = this.form;
            if (!form) return { details: [], totalQuantity: "0.00" };

            const plate = form.VehiclePlateValue || this.allPlateOptions.find((p) => p.Id === form.VehiclePlateId)?.PlateValue || "";
            const driver = this.allEmployees.find((e) => e.Id === form.DriverEmployeeId);
            const details = (form.FuelStorageTransactionDetails || [])
                .filter((d) => d.FuelTypeId && d.Quantity > 0)
                .map((detail) => {
                    const fuelType = this.fuelTypeOptions.find((ft) => ft.value === detail.FuelTypeId);
                    const depot = this.fuelStorageOptions.find((fs) => fs.Id === detail.FuelStorageId);
                    return {
                        fuelTypeName: fuelType ? fuelType.text : "?",
                        depotName: depot ? depot.Name : "?",
                        quantity: (Number(detail.Quantity) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 }),
                    };
                });
            const totalQuantity = (form.FuelStorageTransactionDetails || []).reduce((sum, d) => sum + (Number(d.Quantity) || 0), 0);

            return {
                plate,
                driverName: driver ? driver.FullName : "",
                details,
                totalQuantity: totalQuantity.toLocaleString("tr-TR", { minimumFractionDigits: 2 }),
            };
        },

    },
    created() {
        this.initializeForm();
        const transactionId = this.$route && this.$route.query ? this.$route.query.transactionId : null;
        if (transactionId) {
            this.fetchInitialData().then(() => this.loadForEdit(transactionId));
        } else {
            this.fetchInitialData();
        }
    },
    methods: {
        focusAcCounterInput() {
            this.$nextTick(() => {
                const wrapper = this.$refs.acCounterInput;
                const el = wrapper && wrapper.$refs ? wrapper.$refs.input : null;
                if (el && typeof el.focus === "function") el.focus();
            });
        },
        showConfirmModal() {
            // Klima güncel sayaç, sistemde kayıtlı son değerden düşükse kullanıcıya onay modalı göster.
            // (Klima ünitesi değişebildiği için sayaç düşmesi meşru olabilir — bu yüzden blok değil, uyarı.)
            const lastCounter = this.lastVehicleAcCounter;
            const currentCounter = this.form.VehicleAcCurrentCounter;
            if (
                lastCounter !== null &&
                lastCounter !== undefined &&
                currentCounter !== null &&
                currentCounter !== undefined &&
                !isNaN(parseFloat(currentCounter)) &&
                parseFloat(currentCounter) < parseFloat(lastCounter)
            ) {
                this.$bvModal
                    .msgBoxConfirm("Klima güncel sayaç bilgisi, en son girilen değerden düşüktür. Devam etmek istiyor musunuz?", {
                        title: "Klima Sayaç Uyarısı",
                        okTitle: "Evet",
                        cancelTitle: "Hayır",
                        okVariant: "warning",
                        cancelVariant: "outline-secondary",
                        centered: true,
                        noCloseOnBackdrop: true,
                    })
                    .then((confirmed) => {
                        if (confirmed) {
                            this.$bvModal.show("withdrawal-confirm-modal");
                        } else {
                            this.focusAcCounterInput();
                        }
                    });
                return;
            }
            this.$bvModal.show("withdrawal-confirm-modal");
        },
        save() {
            this.isLoading = true;
            const formToSubmit = JSON.parse(JSON.stringify(this.form));

            formToSubmit.FuelStorageTransactionDetails.forEach((detail) => {
                detail.VehiclePlateId = formToSubmit.VehiclePlateId;
                detail.TrailerPlateId = formToSubmit.TrailerPlateId;
                detail.DriverEmployeeId = formToSubmit.DriverEmployeeId;
                detail.VehicleCurrentKM = formToSubmit.VehicleCurrentKM || 0;
                detail.VehicleCurrentKMManual = formToSubmit.VehicleCurrentKMManual || 0;
                detail.SeyirDriverId = formToSubmit.SeyirDriverId;
                detail.SeyirDriverFullName = formToSubmit.SeyirDriverFullName;
                detail.SeyirDriverKey = formToSubmit.SeyirDriverKey;
                detail.VehicleAcCurrentCounter = formToSubmit.VehicleAcCurrentCounter || 0;
                detail.FuelPump1LT = detail.FuelPump1LT || 0;
                detail.FuelPump1Counter = detail.FuelPump1Counter || 0;
                detail.FuelPump2LT = detail.FuelPump2LT || 0;
                detail.FuelPump2Counter = detail.FuelPump2Counter || 0;
                detail.Ac1FuelPumpLT = detail.Ac1FuelPumpLT || 0;
                detail.Ac2FuelPumpLT = detail.Ac2FuelPumpLT || 0;
            });

            delete formToSubmit.VehiclePlateId;
            delete formToSubmit.TrailerPlateId;
            delete formToSubmit.DriverEmployeeId;
            delete formToSubmit.VehicleCurrentKM;
            delete formToSubmit.VehicleAcCurrentCounter;
            delete formToSubmit.VehicleCurrentKMManual;
            delete formToSubmit.SeyirDriverId;
            delete formToSubmit.SeyirDriverFullName;
            delete formToSubmit.SeyirDriverKey;

            const wasEditing = this.editing;
            FuelStorageApi.FuelStorageTransaction.CreateOrUpdate(this.editing, formToSubmit)
                .then((res) => {
                    if (res.data.Success) {
                        this.$result(res);
                        if (wasEditing) {
                            this.$router.push({ name: "fuel-storage-transaction" });
                        } else {
                            this.resetForm();
                        }
                    } else {
                        this.$result(res);
                    }
                })
                .catch((err) => {
                    this.$result(null, err);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        resetForm() {
            this.initializeForm();
            this.employees = [];
            this.vehiclePlateOptions = [];
        },
        searchEmployees(search, loading) {
            if (search && search.length > 1) {
                loading(true);
                FuelStorageApi.ActiveEmployee.Search(search)
                    .then((res) => {
                        if (res.data.Success) {
                            this.employees = res.data.Data || [];
                        }
                    })
                    .finally(() => loading(false));
            }
        },
        fetchInitialData() {
            this.isLoading = true;
            return Promise.all([this.loadAllEmployees(), this.loadFuelStorageOptions(), this.loadFuelTypeOptions(), this.loadVehiclePlates(), this.loadTrailerPlates()])
                .catch((err) => this.$result(null, "Dropdown seçenekleri yüklenirken bir hata oluştu: " + err))
                .finally(() => (this.isLoading = false));
        },
        loadForEdit(transactionId) {
            this.isLoading = true;
            this.isLoadingForEdit = true;
            return FuelStorageApi.FuelStorageTransaction.Get(transactionId)
                .then((res) => {
                    if (!res.data || !res.data.Success) {
                        this.$result(res);
                        return;
                    }
                    const tx = res.data.Data;
                    if (tx.FuelTransactionType !== 0) {
                        this.$result(null, "Bu kayıt bu sayfadan düzenlenemez (tip uyumsuz).");
                        return;
                    }
                    const details = tx.FuelStorageOutputTransactionDetails || [];
                    const firstDetail = details[0] || {};
                    const selectedVehicle = this.truckPlateOptions.find((v) => v.Id === firstDetail.VehiclePlateId);

                    this.form = {
                        Id: tx.Id,
                        ProcessNumber: tx.ProcessNumber,
                        FuelTransactionType: 0,
                        Description: tx.Description || "",
                        VehiclePlateId: firstDetail.VehiclePlateId || null,
                        VehiclePlateValue: selectedVehicle ? selectedVehicle.PlateValue : (firstDetail.VehiclePlateValue || null),
                        TrailerPlateId: firstDetail.TrailerPlateId || null,
                        DriverEmployeeId: firstDetail.DriverEmployeeId || null,
                        VehicleCurrentKM: firstDetail.VehicleCurrentKM || null,
                        VehicleCurrentKMManual: firstDetail.VehicleCurrentKMManual || null,
                        VehicleAcCurrentCounter: firstDetail.VehicleAcCurrentCounter || null,
                        SeyirDriverFullName: firstDetail.SeyirDriverFullName || null,
                        SeyirDriverId: firstDetail.SeyirDriverId || null,
                        SeyirDriverKey: firstDetail.SeyirDriverKey || null,
                        Address: firstDetail.Address || "",
                        Latitude: firstDetail.Latitude || "",
                        Longitude: firstDetail.Longitude || "",
                        FuelStorageTransactionDetails: details.map((d) => {
                            const storage = d.FuelStorage || this.fuelStorageOptions.find((s) => s.Id === d.FuelStorageId);
                            const fuelTypeId = storage ? storage.FuelStorageFuelTypeId : null;
                            const hasAc = (Number(d.Ac1FuelPumpLT) || 0) + (Number(d.Ac2FuelPumpLT) || 0) > 0;
                            return {
                                Id: d.Id,
                                FuelTypeId: fuelTypeId,
                                FuelStorageId: d.FuelStorageId,
                                Quantity: d.Quantity,
                                FuelPump1LT: d.FuelPump1LT,
                                FuelPump1Counter: d.FuelPump1Counter,
                                FuelPump2LT: d.FuelPump2LT,
                                FuelPump2Counter: d.FuelPump2Counter,
                                Ac1FuelPumpLT: d.Ac1FuelPumpLT,
                                Ac2FuelPumpLT: d.Ac2FuelPumpLT,
                                showAcInputs: hasAc,
                            };
                        }),
                    };

                    // Seyir konum/KM status gösterimlerini uygun şekilde set et
                    if (this.form.Address) {
                        this.locStatus = { isLocked: true, message: null, variant: null };
                    }
                    if (this.form.VehicleCurrentKM) {
                        this.kmStatus = { isLocked: true, message: null, variant: null };
                    }
                    this.lastVehicleAcCounter = this.form.VehicleAcCurrentCounter || null;

                    // Düzenleme modunda Sürücü v-select sadece this.employees (canlı arama
                    // sonuçları) listesinden besleniyor. Liste boş olduğu için
                    // ekrana sürücü adı yerine ID düşüyordu. Kayıtlı sürücüyü allEmployees
                    // havuzundan bulup options listesine enjekte ediyoruz.
                    if (firstDetail.DriverEmployeeId) {
                        const driver = this.allEmployees.find((e) => e.Id === firstDetail.DriverEmployeeId);
                        if (driver && !this.employees.some((e) => e.Id === driver.Id)) {
                            this.employees = [
                                // IsWorking filtresine takılmaması için kopyalarken
                                // bayrağı garanti ediyoruz (computedEmployeeOptions IsWorking'i
                                // arıyor).
                                { ...driver, IsWorking: true },
                                ...this.employees,
                            ];
                        }
                    }

                    // Treyler plakası da benzer şekilde: ListTrailer endpoint'i bazen
                    // eski/pasif treyleri döndürmediği için kayıt düzenlemede plaka
                    // değeri yerine ID gözüküyordu. Backend response'unda gelen
                    // TrailerPlateValue varsa options listesine ekliyoruz.
                    if (firstDetail.TrailerPlateId && firstDetail.TrailerPlateValue) {
                        const existsInTrailers = this.trailerPlateOptions.some((p) => p.Id === firstDetail.TrailerPlateId);
                        if (!existsInTrailers) {
                            this.trailerPlateOptions = [
                                { Id: firstDetail.TrailerPlateId, PlateValue: firstDetail.TrailerPlateValue },
                                ...this.trailerPlateOptions,
                            ];
                        }
                    }
                    // Çekici plakası için de aynı önlem.
                    if (firstDetail.VehiclePlateId && (firstDetail.VehiclePlateValue || (selectedVehicle && selectedVehicle.PlateValue))) {
                        const existsInTrucks = this.truckPlateOptions.some((p) => p.Id === firstDetail.VehiclePlateId);
                        if (!existsInTrucks) {
                            const plateVal = firstDetail.VehiclePlateValue || selectedVehicle.PlateValue;
                            this.truckPlateOptions = [
                                { Id: firstDetail.VehiclePlateId, PlateValue: plateVal },
                                ...this.truckPlateOptions,
                            ];
                        }
                    }

                    this.editing = true;
                })
                .catch((err) => this.$result(null, err))
                .finally(() => {
                    this.isLoading = false;
                    this.$nextTick(() => {
                        this.isLoadingForEdit = false;
                    });
                });
        },
        // loadTruckPlates() {
        //     this.truckPlateLoading = true;
        //     return VehicleOperationsApi.Plate.ListTruck()
        //         .then((res) => {
        //             if (res.data.Success) {
        //                 this.truckPlateOptions = res.data.Data || [];
        //             }
        //         })
        //         .finally(() => {
        //             this.truckPlateLoading = false;
        //         });
        // },
        loadTrailerPlates() {
            this.trailerPlateLoading = true;
            return VehicleOperationsApi.Plate.ListTrailer()
                .then((res) => {
                    if (res.data.Success) {
                        this.trailerPlateOptions = res.data.Data || [];
                    }
                })
                .finally(() => {
                    this.trailerPlateLoading = false;
                });
        },
        loadVehiclePlates() {
            this.truckPlateLoading = true;
            return VehicleOperationsApi.Plate.ListAll() 
                .then((res) => {
                    if (res.data.Success) {
                        this.truckPlateOptions = res.data.Data || [];
                    }
                })
                .finally(() => {
                    this.truckPlateLoading = false;
                });
        },
        loadAllEmployees() {
            return EmployeeOperationsApi.Employee.List().then((res) => {
                if (res.data.Success) {
                    this.allEmployees = res.data.Data || [];
                }
            });
        },

        loadFuelStorageOptions() {
            return FuelStorageApi.FuelStorage.List({}).then((res) => {
                if (res.data.Success) {
                    this.fuelStorageOptions = res.data.Data || [];
                }
            });
        },
        loadFuelTypeOptions() {
            this.fuelTypeOptions = [
                { text: "Motorin", value: "3a82d544-bfda-4995-ad74-29982d9606df" },
                { text: "AdBlue", value: "b8694977-e4fc-4c90-b02f-d7bc04caa146" },
            ];
            return Promise.resolve();
        },
        initializeForm() {
            const now = new Date();
            const timestamp = now.getTime().toString().slice(-8);
            const processNumber = `YC${timestamp}`;

            this.form = {
                ProcessNumber: processNumber,
                FuelTransactionType: 0,
                Description: "",
                VehiclePlateId: null,
                VehiclePlateValue: null,
                TrailerPlateId: null,
                DriverEmployeeId: null,
                VehicleCurrentKM: null,
                VehicleCurrentKMManual: null,
                VehicleAcCurrentCounter: null,
                SeyirDriverFullName: null,
                SeyirDriverId: null,
                SeyirDriverKey: null,
                Address: "",
                Latitude: "",
                Longitude: "",
                FuelStorageTransactionDetails: [this.createNewDetailObject()],
            };
            this.initialForm = JSON.parse(JSON.stringify(this.form));
        },
        createNewDetailObject() {
            return {
                FuelTypeId: null,
                FuelStorageId: null,
                Quantity: 0,
                FuelPump1LT: null,
                FuelPump1Counter: null,
                FuelPump2LT: null,
                FuelPump2Counter: null,
                Ac1FuelPumpLT: null,
                Ac2FuelPumpLT: null,
                showAcInputs: false,
            };
        },
        onFuelTypeChange(detail) {
            detail.FuelStorageId = null;
            detail.FuelPump1LT = null;
            detail.FuelPump1Counter = null;
            detail.FuelPump2LT = null;
            detail.FuelPump2Counter = null;
            detail.showAcInputs = false;
            detail.Ac1FuelPumpLT = null;
            detail.Ac2FuelPumpLT = null;
            detail.Quantity = 0;

            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        handlePlateSelection(selectedId) {
            // Düzenleme modunda form zaten sunucudan geliyor, reset yapma
            if (this.isLoadingForEdit || this.editing) {
                this.form.VehiclePlateId = selectedId;
                return;
            }
            this.form.VehiclePlateId = selectedId;

            const selectedVehicle = this.truckPlateOptions.find((v) => v.Id === selectedId);
            if (selectedVehicle) {
                this.form.VehiclePlateValue = selectedVehicle.PlateValue;
            }

            if (!selectedId) {
                this.form.TrailerPlateId = null;
                this.form.DriverEmployeeId = null;
                this.form.VehicleAcCurrentCounter = null;
            } else {
                FuelStorageApi.Information.VehicleLastAcCounter(selectedId)
                    .then((response) => {
                        if (response.data.Success && response.data.Data && typeof response.data.Data.LastAcCounter !== "undefined") {
                            this.form.VehicleAcCurrentCounter = response.data.Data.LastAcCounter;
                            this.lastVehicleAcCounter = response.data.Data.LastAcCounter;
                        } else {
                            this.form.VehicleAcCurrentCounter = null;
                            this.lastVehicleAcCounter = null;
                        }
                    })
                    .catch(() => {
                        this.form.VehicleAcCurrentCounter = null;
                        this.lastVehicleAcCounter = null;
                    });
            }

            this.validatePlates();
            this.onVehiclePlateSelect(selectedId);
        },
        onVehiclePlateSelect(vehiclePlateId) {
            this.form.VehicleCurrentKM = null;
            this.form.Address = "";
            this.form.Latitude = "";
            this.form.Longitude = "";

            this.kmStatus = { isLocked: false, message: null, variant: null };
            this.locStatus = { isLocked: false, message: null, variant: null };
            this.acCounterStatus = { message: null, variant: null };
            this.lastVehicleAcCounter = null;

            if (!vehiclePlateId) return;

            const selectedVehicle = this.truckPlateOptions.find((v) => v.Id === vehiclePlateId);
            const plateValue = selectedVehicle ? selectedVehicle.PlateValue : null;

            if (!plateValue) return;

            this.isLoading = true;

            SeyirOperationsApi.ActualLocation({ Plate: plateValue })
                .then((response) => {
                    const dataList = response.data.Data;

                    let latestData = null;
                    if (response.data.Success && Array.isArray(dataList) && dataList.length > 0) {
                        latestData = dataList.sort((a, b) => new Date(b.DataTime) - new Date(a.DataTime))[0];
                    }

                    if (latestData && latestData.TotalKm && parseFloat(latestData.TotalKm) > 0) {
                        this.form.VehicleCurrentKM = parseFloat(latestData.TotalKm);
                        this.kmStatus.isLocked = true;
                    } else {
                        this.kmStatus.isLocked = false;
                        this.kmStatus.message = "Araç KM bilgisine erişilemedi.";
                        this.kmStatus.variant = "danger";
                    }

                    if (latestData && latestData.Address) {
                        this.form.Address = latestData.Address;
                        this.form.Latitude = latestData.Latitude || "";
                        this.form.Longitude = latestData.Longitude || "";
                        this.locStatus.isLocked = true;
                    } else {
                        this.locStatus.isLocked = false;
                        this.locStatus.message = "Araç konum bilgisine erişilemedi.";
                        this.locStatus.variant = "danger";
                    }

                    if (latestData) {
                        this.form.SeyirDriverId = latestData.DriverId || null;
                        this.form.SeyirDriverFullName = latestData.Driver || null;
                        this.form.SeyirDriverKey = latestData.DriverKey || null;
                    }
                })
                .catch((error) => {
                    console.error("Seyir Hatası:", error);
                    this.kmStatus = { isLocked: false, message: "Araç KM bilgisine erişilemedi.", variant: "danger" };
                    this.locStatus = { isLocked: false, message: "Araç konum bilgisine erişilemedi.", variant: "danger" };
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        handleAcCounterInput(value) {
            this.form.VehicleAcCurrentCounter = value;
            if (this.lastVehicleAcCounter !== null && value !== null && parseFloat(value) < parseFloat(this.lastVehicleAcCounter)) {
                this.acCounterStatus.message = `Girilen sayaç değeri (${value}) sistemdeki son kayıtlı değerden (${this.lastVehicleAcCounter}) düşüktür. Lütfen kontrol ediniz.`;
                this.acCounterStatus.variant = "warning";
            } else {
                this.acCounterStatus = { message: null, variant: null };
            }
        },
        handleManualInput(type, value) {
            if (type === "km") {
                if (value && parseFloat(value) > 0) {
                    this.kmStatus.message = "Araç km bilgisi manuel girilmiştir, onaylamadan önce kontrol ediniz.";
                    this.kmStatus.variant = "warning";
                } else {
                    this.kmStatus.message = "Araç KM bilgisine erişilemedi.";
                    this.kmStatus.variant = "danger";
                }
            } else if (type === "loc") {
                if (value && value.trim().length > 0) {
                    this.locStatus.message = "Adres bilgisi manuel girilmiştir, onaylamadan önce kontrol ediniz.";
                    this.locStatus.variant = "warning";
                } else {
                    this.locStatus.message = "Araç konum bilgisine erişilemedi.";
                    this.locStatus.variant = "danger";
                }
            }
        },
        validatePlates() {
            this.$delete(this.plateErrors, "VehiclePlateId");
            this.$delete(this.plateErrors, "TrailerPlateId");

            if (this.form.VehiclePlateId && this.form.TrailerPlateId && this.form.VehiclePlateId === this.form.TrailerPlateId) {
                this.$set(this.plateErrors, "VehiclePlateId", "Araç ve treyler plakası aynı olamaz");
                this.$set(this.plateErrors, "TrailerPlateId", "Araç ve treyler plakası aynı olamaz");
            }
        },

        onTrailerPlateChange(selectedId) {
            this.form.TrailerPlateId = selectedId;
            this.validatePlates();
        },
        filteredFuelStorageOptions(fuelTypeId) {
            if (!fuelTypeId) return [];
            return this.fuelStorageOptions
                .filter((depo) => depo.FuelStorageFuelTypeId === fuelTypeId)
                .map((depo) => ({
                    ...depo,
                    DisplayName: `${depo.Name} [${(depo.Capacity || 0).toLocaleString("tr-TR")} lt]`,
                }));
        },
        addNewDetail() {
            const newDetail = this.createNewDetailObject();
            this.form.FuelStorageTransactionDetails.push(newDetail);
        },

        removeDetail(index) {
            if (this.form.FuelStorageTransactionDetails.length > 1) {
                this.form.FuelStorageTransactionDetails.splice(index, 1);
            }
        },

        getTotalQuantity(detail) {
            const total = this.getTotalQuantityNumeric(detail);
            detail.Quantity = total;
            return total.toFixed(2);
        },

        getTotalQuantityNumeric(detail) {
            const pump1 = Number(detail.FuelPump1LT) || 0;
            const pump2 = Number(detail.FuelPump2LT) || 0;

            const ac1 = Number(detail.Ac1FuelPumpLT) || 0;
            const ac2 = Number(detail.Ac2FuelPumpLT) || 0;

            return pump1 + pump2 + ac1 + ac2;
        },
        onShowAcInputsChange(detail) {
            if (!detail.showAcInputs) {
                detail.Ac1FuelPumpLT = 0;
                detail.Ac2FuelPumpLT = 0;
            }
            this.$forceUpdate();
        },
        getDepotInfo(storageId) {
            if (!storageId || !this.fuelStorageOptions) {
                return null;
            }
            return this.fuelStorageOptions.find((depo) => depo.Id === storageId);
        },
        isPumpAmountExceedingStock(detail) {
            if (!detail.FuelStorageId) return false;

            const depotInfo = this.getDepotInfo(detail.FuelStorageId);
            if (!depotInfo) return false;

            const stock = Number(depotInfo.FuelQuantity) || 0;
            const pumpTotal = (Number(detail.FuelPump1LT) || 0) + (Number(detail.FuelPump2LT) || 0);

            return pumpTotal > stock;
        },
        getDepotName(storageId) {
            if (!storageId) return "";
            const depot = this.fuelStorageOptions.find((d) => d.Id === storageId);
            return depot ? depot.Name : "";
        },
        onDepotSelect(detail) {
            const depotInfo = this.getDepotInfo(detail.FuelStorageId);
            if (depotInfo) {
                detail.FuelPump1Counter = depotInfo.FuelPump1Counter || null;
                detail.FuelPump2Counter = depotInfo.FuelPump2Counter || null;
            } else {
                detail.FuelPump1Counter = null;
                detail.FuelPump2Counter = null;
            }
        },
    },
};
</script>

<style scoped>
.position-relative {
    position: relative;
}

.detail-card {
    border: 2px solid #e3f2fd;
    border-radius: 12px;
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.form-section-title {
    color: #1976d2;
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e3f2fd;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.detail-input-section {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
}

.form-control {
    border-radius: 6px;
    border: 1px solid #ddd;
    transition: all 0.2s ease;
}

.form-control:focus {
    border-color: #2196f3;
    box-shadow: 0 0 0 0.2rem rgba(33, 150, 243, 0.25);
}

@media (max-width: 768px) {
    .detail-card {
        margin-bottom: 1.5rem;
    }

    .form-section-title {
        font-size: 0.8rem;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.depot-select-wrapper {
    min-height: 38px;
}
.depot-select-wrapper ::v-deep .vs__dropdown-toggle {
    min-height: 38px;
}

.depot-select-wrapper ::v-deep .vs__selected-options {
    overflow: hidden;
}

.depot-select-wrapper ::v-deep .vs__selected {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 93%;
}
</style>
